import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart, Trash2, ArrowRight } from 'lucide-react';
import PropertyCard from '@/components/PropertyCard';
import { getProperties, getSavedProperties } from '@/lib/api';
import { Button } from '@/components/ui/button';

const SavedPropertiesPage = () => {
  const [savedProperties, setSavedProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchSavedProperties = async () => {
    setLoading(true);
    try {
      const savedRes = await getSavedProperties();
      const savedIds = savedRes.data.saved_ids || [];
      
      if (savedIds.length === 0) {
        setSavedProperties([]);
        setLoading(false);
        return;
      }
      
      // Fetch all properties and filter
      const propertiesRes = await getProperties({});
      const filtered = propertiesRes.data.filter(p => savedIds.includes(p.id));
      setSavedProperties(filtered);
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') console.error('Saved properties fetch failed:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSavedProperties();
    
    // Listen for changes
    const handleChange = () => fetchSavedProperties();
    window.addEventListener('savedPropertyChange', handleChange);
    return () => window.removeEventListener('savedPropertyChange', handleChange);
  }, []);

  return (
    <div className="min-h-screen bg-warm-sand pt-24 pb-16" data-testid="saved-properties-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center gap-3 mb-2">
            <Heart className="w-8 h-8 text-ubud-jungle" />
            <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-charcoal">
              Saved Properties
            </h1>
          </div>
          <p className="font-body text-charcoal-light">
            {loading ? 'Loading...' : `${savedProperties.length} properties saved`}
          </p>
        </motion.div>

        {/* Content */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white rounded-2xl h-96 animate-pulse" />
            ))}
          </div>
        ) : savedProperties.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl p-12 text-center"
          >
            <Heart className="w-16 h-16 text-charcoal-muted mx-auto mb-4" />
            <h2 className="font-heading text-2xl font-medium text-charcoal mb-2">
              No Saved Properties Yet
            </h2>
            <p className="text-charcoal-light mb-8 max-w-md mx-auto">
              When you find properties you love, click the heart icon to save them here for easy access later.
            </p>
            <Link
              to="/properties"
              className="inline-flex items-center gap-2 bg-ubud-jungle text-white hover:bg-ubud-jungle-dark transition-all duration-300 rounded-full px-8 py-3 font-medium"
            >
              Browse Properties
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {savedProperties.map((property, idx) => (
              <PropertyCard key={property.id} property={property} index={idx} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SavedPropertiesPage;
